const API_BASE = "/api";

// Suite login URL for 401 redirect
// C-1 (2026-05-11) — if the build-time env var is missing, surface a clear
// warning in the console rather than silently falling back to localhost:3000
// (which strands deployed operators on a dead URL).
const SUITE_URL = import.meta.env["VITE_SUITE_URL"] || "http://localhost:3000";
if (!import.meta.env["VITE_SUITE_URL"] && typeof window !== "undefined") {
  console.warn(
    "[capture] VITE_SUITE_URL not set at build time — login redirects will target localhost:3000. " +
      "Check Dockerfile ARG + Railway build-arg config.",
  );
}

/**
 * Suite SSO login URL for the 401 bounce + the login button.
 *
 * Suite honours the `?callback=` param (it mints the ES256 JWT and redirects to
 * `<callback>?token=`, which our /api/auth/callback verifies + turns into a local
 * session). It does NOT honour `?redirect=` — that param is silently ignored and
 * the user strands on the Suite dashboard (verified against suite-pp 2026-06-10).
 * The callback is our own origin's auth callback, so this works across
 * preprod/prod with no extra config.
 */
export function suiteLoginUrl(): string {
  const callback = `${window.location.origin}/api/auth/callback`;
  return `${SUITE_URL}/login?callback=${encodeURIComponent(callback)}`;
}

/**
 * F-CAPTURE-005 — Suite's Skarn-wide sign-out. Destroying Capture's own session
 * is not enough: Suite's session is still live and signs the user straight back
 * in. Suite's `/logout` revokes its session and returns the browser to
 * `returnTo`, which Suite only honours for a registered product origin.
 */
export function suiteLogoutUrl(returnTo: string): string {
  return `${SUITE_URL}/logout?return=${encodeURIComponent(returnTo)}`;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`API error ${status}`);
    this.name = "ApiError";
  }
}

/**
 * Pulls the `{ error: string }` code off an ApiError's body — the shape every
 * route in this app uses for 4xx policy responses (409 conflicts, 400
 * validation). Callers map the code to operator-friendly copy; this just does
 * the (previously duplicated) unwrapping.
 */
export function apiErrorCode(err: unknown): string {
  const apiErr = err as ApiError;
  const body = apiErr?.body as { error?: string } | null;
  return body?.error ?? "unknown";
}

/**
 * Returns true when the given thrown value represents an HTTP 4xx response.
 *
 * Info-only panels (calibration status, errors history, etc.) should treat 4xx
 * as a "no data" empty state rather than surfacing a scary "couldn't be loaded"
 * message — 4xx usually means "this resource doesn't exist for you yet" (stale
 * URL, tenant mismatch, feature not configured), not an outage. 5xx and network
 * errors are real failures and should still surface error UI.
 *
 * Recognises both `ApiError` instances (throw site uses `new ApiError(status, ...)`)
 * and plain `Error` objects whose message starts with "API error 4".
 */
export function is4xxApiError(err: unknown): boolean {
  if (err instanceof ApiError) {
    return err.status >= 400 && err.status < 500;
  }
  if (err instanceof Error) {
    return /^API error 4\d\d\b/.test(err.message);
  }
  return false;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    ...options,
  });

  // 401 → bounce to Suite login (NON-NEGOTIABLE)
  if (response.status === 401) {
    window.location.href = suiteLoginUrl();
    // Never reaches here — but satisfy the type system
    throw new ApiError(401, "Redirecting to login");
  }

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(response.status, body);
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),

  // Bodyless POSTs still need a valid JSON body because every request
  // sets Content-Type: application/json — Fastify rejects an empty body
  // with FST_ERR_CTP_EMPTY_JSON_BODY (400). See audit #17, 2026-04-22.
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    }),

  put: <T>(path: string, body: unknown) =>
    request<T>(path, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  patch: <T>(path: string, body: unknown) =>
    request<T>(path, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  // Bodyless DELETEs still need a valid JSON body for the same reason as POSTs —
  // every request sets Content-Type: application/json and Fastify rejects an
  // empty body with FST_ERR_CTP_EMPTY_JSON_BODY (400).
  delete: <T>(path: string) =>
    request<T>(path, {
      method: "DELETE",
      body: JSON.stringify({}),
    }),

  // POST a raw text body with a custom content-type. Used for paste-style
  // imports (e.g., R-31 lookup-table CSV paste) where the operator hands
  // us CSV text instead of a file. JSON.stringify-ing a raw string would
  // wrap it in quotes; we want the server to see the raw bytes.
  postRaw: async <T>(path: string, body: string, contentType: string): Promise<T> => {
    const response = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": contentType },
      body,
    });

    if (response.status === 401) {
      window.location.href = suiteLoginUrl();
      throw new ApiError(401, "Redirecting to login");
    }

    if (!response.ok) {
      const errBody = await response.json().catch(() => null);
      throw new ApiError(response.status, errBody);
    }

    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  },

  // Fetches a non-JSON response body as a Blob, carrying its Content-Type
  // through. Used for the export-payload proxy (E-027), whose content-type
  // varies with the destination's export format (CSV/XML/JSON/binary).
  getBlob: async (path: string): Promise<{ blob: Blob; contentType: string; headers: Headers }> => {
    const response = await fetch(`${API_BASE}${path}`, { credentials: "include" });

    if (response.status === 401) {
      window.location.href = suiteLoginUrl();
      throw new ApiError(401, "Redirecting to login");
    }

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new ApiError(response.status, body);
    }

    const blob = await response.blob();
    return {
      blob,
      contentType: response.headers.get("Content-Type") ?? "application/octet-stream",
      headers: response.headers,
    };
  },

  // Multipart upload (no JSON content-type)
  upload: async <T>(path: string, formData: FormData): Promise<T> => {
    const response = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    if (response.status === 401) {
      window.location.href = suiteLoginUrl();
      throw new ApiError(401, "Redirecting to login");
    }

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new ApiError(response.status, body);
    }

    return response.json() as Promise<T>;
  },
};
